import type { StaffUser, UserRole } from './auth.model';

export type PermissionKey = string;

export interface PermissionGroup {
  id: string;
  label: string;
  permissions: { key: string; label: string }[];
}

export interface RoleConfigEntry {
  role: UserRole;
  label: string;
  description: string;
  color: string;
  editable: boolean;
  permissions: { key: string; label: string }[];
}

export interface RoleConfig {
  permissionLabels: Record<string, string>;
  permissionGroups: PermissionGroup[];
  editableRoles: UserRole[];
  permissions: Record<string, string[]>;
  roles: RoleConfigEntry[];
  isCustomized: boolean;
}

export const NAV_ITEMS: {
  path: string;
  permission: PermissionKey | null;
}[] = [
  { path: '/dashboard', permission: null },
  { path: '/cuisine/consommation', permission: 'stock.consume' },
  { path: '/cuisine', permission: 'kitchen.screen' },
  { path: '/bar', permission: 'bar.screen' },
  { path: '/commandes', permission: 'orders.view' },
  { path: '/caisse', permission: 'payments.manage' },
  { path: '/reporting', permission: 'reports.view' },
  { path: '/stock', permission: 'stock.manage' },
  { path: '/menu', permission: 'menu.manage' },
  { path: '/tables', permission: 'tables.manage' },
  { path: '/staff', permission: 'staff.manage' },
  { path: '/theme', permission: 'theme.manage' },
  { path: '/settings', permission: 'settings.manage' },
];

export function hasPermission(
  user: StaffUser | null | undefined,
  key: PermissionKey,
): boolean {
  if (!user) return false;
  if (user.role === 'owner') return true;
  return user.permissions?.includes(key) ?? false;
}

export function hasAnyPermission(
  user: StaffUser | null | undefined,
  keys: PermissionKey[],
): boolean {
  return keys.some((k) => hasPermission(user, k));
}

export function canSeeNav(user: StaffUser | null | undefined, path: string): boolean {
  if (!user) return false;
  const item = NAV_ITEMS.find((n) => path.startsWith(n.path));
  if (!item || !item.permission) return true;
  if (path.startsWith('/tables') && hasPermission(user, 'tables.status')) return true;
  return hasPermission(user, item.permission);
}

/** orders.manage active → orders.view coché automatiquement */
export function normalizePermissionToggle(
  current: string[],
  key: string,
  enabled: boolean,
): string[] {
  const set = new Set(current);
  if (enabled) {
    set.add(key);
    if (key === 'orders.manage') set.add('orders.view');
    if (key === 'reports.export') {
      set.add('reports.ai');
      set.add('reports.view');
    }
    if (key === 'reports.ai') set.add('reports.view');
    if (key === 'stock.manage') set.add('stock.view');
  } else {
    set.delete(key);
    if (key === 'orders.view') set.delete('orders.manage');
    if (key === 'reports.view') {
      set.delete('reports.ai');
      set.delete('reports.export');
    }
    if (key === 'reports.ai') set.delete('reports.export');
    if (key === 'stock.view') set.delete('stock.manage');
  }
  return [...set];
}

export function permissionSummary(keys: string[], labels: Record<string, string>): string {
  if (!keys.length) return 'Aucun accès';
  return keys.slice(0, 3).map((k) => labels[k] ?? k).join(', ') + (keys.length > 3 ? '…' : '');
}
